import { useAsyncEffect } from '@xylabs/sdk-react'
import { PropsWithChildren, useContext, useEffect, useState } from 'react'

import { EntitySortOrder, EntityType, useEntities, XyoWorldEntity } from '../../Hooks'
import { GeotokensContext } from '../Geotokens'
import { EntityListContext } from './Context'
import { getSortByLandAreaEntities, SortableEntity, sortEntitiesLandAreaHighToLow, sortEntitiesLandAreaLowToHigh } from './EntitySorting'

interface Props {
  enabled?: boolean
}

const EntityListProvider: React.FC<PropsWithChildren<Props>> = (props) => {
  const { children } = props
  const enabled = true
  const { geotokens } = useContext(GeotokensContext)

  const [filter, setFilter] = useState(EntityType.All)
  const [entityList, setEntityList] = useState<XyoWorldEntity[]>()
  const [sortOrder, setSortOrder] = useState(EntitySortOrder.LandAreaHighToLow)
  const [sortedEntityList, setSortedEntityList] = useState<SortableEntity[]>()
  const [updateCount, setUpdateCount] = useState(0)

  const entities = useEntities(geotokens)

  const updateFilter = (entityType: EntityType) => {
    if (filter !== entityType) {
      setEntityList(undefined)
      setFilter(entityType)
      switch (entityType) {
        case EntityType.Bidder:
          setSortOrder(EntitySortOrder.LandAreaHighToLow)
          break
        case EntityType.Owner:
          setSortOrder(EntitySortOrder.LandAreaHighToLow)
          break
        case EntityType.Player:
          setSortOrder(EntitySortOrder.LandAreaHighToLow)
          break
      }
    }
  }

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      if (enabled) {
        let entityList = undefined
        if (sortOrder && filter && entities) {
          entityList = await entities.sortedList(sortOrder, filter)
        }
        if (isMounted()) {
          setEntityList(entityList)
        }
      }
    },
    [entities, filter, sortOrder, updateCount, enabled],
  )

  const updateSortOrder = (order: EntitySortOrder) => {
    setSortedEntityList([]) //this causes the consumers to clear their lists while busy
    if (sortOrder !== order) {
      setSortOrder(order)
    }
  }

  useEffect(() => {
    let sortedEntityList
    if (entityList && enabled) {
      switch (sortOrder) {
        case EntitySortOrder.LandAreaHighToLow:
          sortedEntityList = sortEntitiesLandAreaHighToLow(getSortByLandAreaEntities(entityList))
          break
        case EntitySortOrder.LandAreaLowToHigh:
          sortedEntityList = sortEntitiesLandAreaLowToHigh(getSortByLandAreaEntities(entityList))
          break
      }
    }
    setSortedEntityList(sortedEntityList)
  }, [sortOrder, entityList, updateCount, enabled])

  const update = () => {
    setSortedEntityList(undefined)
    setEntityList(undefined)
    setUpdateCount(updateCount + 1) //force effects to run
  }

  return (
    <EntityListContext.Provider
      value={{
        entityList,
        filter,
        sortOrder,
        sortedEntityList,
        update,
        updateFilter,
        updateSortOrder,
      }}
    >
      {children}
    </EntityListContext.Provider>
  )
}

export const EntityListLoader: React.FC<PropsWithChildren<Props>> = (props) => {
  const { children } = props
  return <EntityListProvider>{children}</EntityListProvider>
}
